import { Description, Field, Label } from '@headlessui/react';
import classnames from 'classnames';
import React, { forwardRef } from 'react';

import { useThemeClasses } from '@/lib/legacy-theme/theme-utils';

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helperText?: string;
  error?: string;
  success?: string;
  variant?: 'default' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  rows?: number;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, helperText, error, success, variant = 'default', size = 'md', fullWidth = false, rows = 4, className, disabled, ...props }, ref) => {
    // Base classes using theme-aware variables
    const baseClasses = 'rounded-theme-md border font-theme-normal transition-theme-normal focus:outline-none focus:ring-2 focus:ring-offset-2 resize-vertical';

    // Theme-aware variant classes
    const variantClasses = {
      default: 'border-border bg-surface text-text placeholder-text-muted focus:border-primary focus:ring-primary',
      error: 'border-error bg-surface text-text placeholder-text-muted focus:border-error focus:ring-error',
      success: 'border-success bg-surface text-text placeholder-text-muted focus:border-success focus:ring-success',
    };

    // Theme-aware size classes
    const sizeClasses = {
      sm: 'px-theme-sm py-theme-xs text-theme-sm',
      md: 'px-theme-md py-theme-sm text-theme-base',
      lg: 'px-theme-lg py-theme-md text-theme-lg',
    };

    // Determine the actual variant based on error/success props
    const actualVariant = error ? 'error' : success ? 'success' : variant;

    // Combine textarea classes
    const textareaClasses = useThemeClasses(classnames(baseClasses, variantClasses[actualVariant], sizeClasses[size], fullWidth && 'w-full', disabled && 'opacity-50 cursor-not-allowed', className));

    // Container classes
    const containerClasses = useThemeClasses(classnames('relative', fullWidth && 'w-full'));

    // Label classes
    const labelClasses = useThemeClasses(classnames('block font-theme-medium text-theme-sm text-text mb-theme-xs', disabled && 'opacity-50'));

    // Helper text classes
    const helperTextClasses = useThemeClasses(classnames('mt-theme-xs text-theme-xs', error ? 'text-error' : success ? 'text-success' : 'text-text-muted'));

    return (
      <Field className={containerClasses}>
        {label && <Label className={labelClasses}>{label}</Label>}

        <textarea ref={ref} className={textareaClasses} disabled={disabled} rows={rows} {...props} />

        {(helperText || error || success) && <Description className={helperTextClasses}>{error || success || helperText}</Description>}
      </Field>
    );
  },
);

TextArea.displayName = 'TextArea';

export default TextArea;
